'use client';

import { IconButton } from '@eliadmin/core/src/components/nt/ui/icon-button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

type CarouselProps = {
  /** `CarouselItem`s. */
  children: ReactNode;
  className?: string;
  /** Item gap, bleed and shadow room of the scrolling list. */
  trackClassName?: string;
  /** Lists without links inside: the list itself takes keyboard focus (arrow keys scroll it). */
  focusableTrack?: boolean;
  /** Accessible name of a focusable list. */
  label?: string;
  /**
   * Section heading row above the track (Figma 116:1397): the heading on the left, prev/next as
   * Outline M buttons on the right from md – instead of the buttons on the track edges. Spacing to
   * the track comes from `className` (e.g. `flex flex-col gap-8`).
   */
  header?: ReactNode;
};

/** Prev/next sit centred on the left/right edge of the track (Figma 23:1784, 46:3758), from md. */
const EDGE_BUTTON =
  'absolute top-1/2 z-10 hidden -translate-y-1/2 transition-opacity motion-reduce:transition-none disabled:opacity-0 md:inline-flex';

/**
 * Native scroll-snap slider (touch, trackpad, focus scrolling), no autoplay. „Next“ moves by one item;
 * at an end its button is disabled and hidden – Figma shows only „next“ at the start. Before the
 * first measurement the list counts as overflowing, which is that Figma state.
 */
export function Carousel({
  children,
  className,
  trackClassName,
  focusableTrack,
  label,
  header,
}: CarouselProps) {
  const { t } = useTranslations();
  const trackId = useId();
  const trackRef = useRef<HTMLUListElement>(null);
  const [edges, setEdges] = useState({ start: true, end: false });

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const end = track.scrollWidth - track.clientWidth;
        setEdges({ start: track.scrollLeft <= 1, end: track.scrollLeft >= end - 1 });
      });
    };
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    track.addEventListener('scroll', measure, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      track.removeEventListener('scroll', measure);
    };
  }, []);

  const scrollByItem = (direction: 1 | -1) => {
    const track = trackRef.current;
    const item = track?.firstElementChild;
    if (!track || !(item instanceof HTMLElement)) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    track.scrollBy({ left: direction * (item.offsetWidth + gap) });
  };

  const button = (direction: 1 | -1, buttonClassName: string, variant: 'primary' | 'outline') => (
    <IconButton
      icon={direction === 1 ? 'caret-right' : 'caret-left'}
      label={t(direction === 1 ? 'text3526' : 'text3525')}
      variant={variant}
      aria-controls={trackId}
      disabled={direction === 1 ? edges.end : edges.start}
      onClick={() => scrollByItem(direction)}
      className={buttonClassName}
    />
  );

  return (
    <div className={cn('relative', className)}>
      {header ? (
        <div className="flex items-end justify-between gap-6">
          <div className="min-w-0">{header}</div>
          {edges.start && edges.end ? null : (
            <div className="hidden shrink-0 gap-3 md:flex">
              {button(-1, '', 'outline')}
              {button(1, '', 'outline')}
            </div>
          )}
        </div>
      ) : null}
      <ul
        id={trackId}
        ref={trackRef}
        tabIndex={focusableTrack ? 0 : undefined}
        aria-label={focusableTrack ? label : undefined}
        className={cn(
          'hide-scroll-bar flex snap-x snap-mandatory overflow-x-auto motion-safe:scroll-smooth',
          focusableTrack && 'rounded-2xl focus-visible:shadow-focus focus-visible:outline-none',
          trackClassName,
        )}
      >
        {children}
      </ul>
      {header ? null : (
        <>
          {button(-1, cn(EDGE_BUTTON, 'left-0 -translate-x-1/2'), 'primary')}
          {button(1, cn(EDGE_BUTTON, 'right-0 translate-x-1/2'), 'primary')}
        </>
      )}
    </div>
  );
}
